/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import spaghetti_codeImg from 'assets/images/spaghetti-code.png'
import oop_objectsImg from 'assets/images/oop-objects.png'
import inheritanceImg from 'assets/images/inheritance.png'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - ООП</h2>
    <p className={styles.description}>
      <b>Процедурное программирование</b> — набор не связанных явно функций и переменных для хранения и обработки
      информации. Этот подход прост и прямолинеен, но при росте кодовой базы, результатом может быть то, что называется{' '}
      <b>spaghetti code</b> — тесно связанный код.{' '}
    </p>
    <img className={styles.img} src={spaghetti_codeImg} alt="spaghetti-code" />
    <p className={styles.description}>
      Ниже имеем процедурный код, не связанные переменные и функция для подсчета результата.{' '}
    </p>
    <CopyBlock
      text={`const baseSalary = 30000;
const overtime = 10;
const rate = 20;

const getWage = (baseSalary, overtime, rate) => {
  return baseSalary + overtime * rate;
};

getWage(baseSalary, overtime, rate);`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      <b>Объектно-ориентированное программирование (ООП)</b> — методология, основанная на представлении программы в виде
      совокупности объектов, каждый из которых содержит данные (свойства) и методы для взаимодействия с ними.{' '}
    </p>
    <img className={styles.img} src={oop_objectsImg} alt="oop-objects" />
    <p className={styles.description}>Используем ООП собрав данные в объект employee. </p>
    <CopyBlock
      text={`const employee = {
  baseSalary: 30000,
  overtime: 10,
  rate: 20,
  getWage() {
    return this.baseSalary + this.overtime * this.rate;
  },
};

employee.getWage();`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <p className={styles.description}>
      При таком подходе у метода нету параметров, используются свойства объекта, которые настраиваются при создании
      объекта и возможно так же изменяются другими методами. На выходе имеем сущность с простым интерфейсом, что
      понижает сложность программы и повышает повторное использование кода в других частях программы.{' '}
    </p>
    <p className={styles.notificationInfo}>
      Идеологически, ООП это подход к программированию как к моделированию, решающий основную задачу - структурирование
      информации с точки зрения управляемости, что существенно улучшает контроль процесса моделирования. Это крайне
      важно при при реализации крупных проектов.{' '}
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Основные понятия ООП</h3>
      <p className={styles.description}>
        Представьте себе, что мы проектируем автомобиль. У него будет двигатель, четыре колеса, бензобак и т. д.
        Автомобиль должен иметь возможность заводиться, набирать и сбавлять скорость. Мы знаем как взаимодействует
        двигатель и колёса, то есть согласно каким законам взаимодействуют разные части машины.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.1 - Класс</h4>
        <p className={styles.description}>
          Мы описываем все запчасти из которых состоит автомобиль, то каким образом эти запчасти взаимодействуют между
          собой и что должен сделать водитель, чтобы машина затормозила, включились фары и другое. Результатом нашей
          работы будет некоторый эскиз (шаблон, схема). Мы только что разработали то, что в ООП называется класс.{' '}
        </p>
        <p className={styles.description}>
          <b>Класс</b> — способ описания сущности, определяющий состояние и поведение, зависящее от этого состояния, а
          также правила для взаимодействия с данной сущностью (контракт).{' '}
        </p>
        <p className={styles.description}>
          В нашем случае, класс описывает сущность – автомобиль. Свойствами класса будут двигатель, колеса, фары и т. д.
          Методами класса будут открыть дверь, завести двигатель, увеличить скорость и т. п.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.2 - Объект</h4>
        <p className={styles.description}>
          Мы спроектировали чертежи, и машины, разработанные по ним, сходят с конвейера. Каждая из них точно повторяет
          чертеж, все системы взаимодействуют именно так, как мы спроектировали, но каждая машина уникальна. Они все
          имеют номер кузова и двигателя, но все номера разные, автомобили различаются цветом, отделкой салона. Эти
          автомобили являются экземплярами класса.{' '}
        </p>
        <p className={styles.description}>
          <b>Объект (экземпляр)</b> — это отдельный представитель класса, имеющий конкретное состояние и поведение,
          полностью определяемое классом. Это то, что создано по чертежу, то есть по описанию из класса.
        </p>
        <p className={styles.description}>
          Говоря простым языком, объект имеет конкретные значения свойств и методы, работающие с этими свойствами на
          основе правил, заданных в классе. В данном примере, если класс это некоторый абстрактный автомобиль на
          чертеже, то объект — это конкретный автомобиль, стоящий у нас под окнами.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.1.3 - Интерфейс</h4>
        <p className={styles.description}>
          Когда мы подходим к автомату с кофе или садимся за руль автомобиля, существует некоторый набор элементов
          управления, с которыми мы можем взаимодействовать.{' '}
        </p>
        <p className={styles.description}>
          <b>Интерфейс</b> — это набор свойств и методов класса, доступных для использования при работе с экземпляром.
        </p>
        <p className={styles.description}>
          По сути, интерфейс специфицирует класс, чётко определяя все возможные действия над ним. Хороший пример
          интерфейса — приборная панель автомобиля, которая позволяет вызвать такие методы как увеличение скорости,
          торможение, поворот, переключение передач, включение фар и т. п.{' '}
        </p>
        <p className={styles.description}>
          При описании интерфейса класса очень важно соблюсти баланс между гибкостью и простотой. Класс с простым
          интерфейсом будет легко использовать, но будут существовать задачи, которые с помощью него решить будет не под
          силу. Если интерфейс будет гибким, то скорее всего, он будет состоять из достаточно сложных методов с большим
          количеством параметров, которые будут позволять делать очень многое, но его использование будет сопряжено с
          большими сложностями и риском совершить ошибку, что-то перепутав.{' '}
        </p>
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.2 - Парадигмы</h3>
      <p className={styles.description}>
        ООП построено на четырех основных понятиях: инкапсуляция, абстракция, наследование и полиморфизм.{' '}
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.1 - Инкапсуляция</h4>
        <p className={styles.description}>
          Внутренние процессы работы автомобиля довольно сложны. Но все эти действия скрыты от пользователя и позволяют
          ему крутить руль и нажимать на педаль газа, не задумываясь, что в это время происходит внутри. Именно сокрытие
          внутренних процессов, происходящих в автомобиле, позволяет эффективно его использовать даже новичкам. Это и
          есть инкапсуляция.{' '}
        </p>
        <p className={styles.description}>
          <b>Инкапсуляция (encapsulation)</b> — это свойство системы, позволяющее объединить данные и методы, работающие
          с ними, в классе и скрыть детали реализации от пользователя.{' '}
        </p>
        <p className={styles.description}>
          Инкапсуляция неразрывно связана с понятием интерфейса класса. Всё то, что не входит в интерфейс,
          инкапсулируется (скрыто) в классе. Пользователь может работать со всем функционалом через интерфейс, не
          задумываясь над тем, как реализован функционал.{' '}
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.2 - Абстракция</h4>
        <p className={styles.description}>
          Представьте, что водитель едет в автомобиле по оживлённому участку движения. Понятно, что в этот момент он не
          будет задумываться о химическом составе краски автомобиля, особенностях взаимодействия шестерёнок в коробке
          передач или влияния формы кузова на скорость. Однако, руль, педали, указатель поворота он будет использовать
          регулярно.{' '}
        </p>
        <p className={styles.description}>
          <b>Абстрагирование (abstraction)</b> — это способ выделить набор значимых характеристик объекта, исключая из
          рассмотрения незначимые.
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.3 - Наследование</h4>
        <p className={styles.description}>
          Представим что инженерам поставили задачу разработки и выпуска модельного ряда более современных автомобилей.
          При этом у них уже есть старая модель, которая отлично зарекомендовала себя. Очевидно, что инженеры не будут
          проектировать новый автомобиль с нуля, а взяв за основу предыдущее поколение, внесут ряд конструктивных
          изменений.{' '}
        </p>
        <p className={styles.description}>
          Все модификации будут иметь большинство свойств прежней модели. При этом каждая из моделей будет реализовать
          некоторую новую функциональность или конструктивную особенность. В данном случае, мы имеем дело с
          наследованием.{' '}
        </p>
        <p className={styles.description}>
          <b>Наследование (inheritance)</b> — это свойство системы, позволяющее описать новый класс на основе уже
          существующего, с частично или полностью заимствующимся функционалом. Класс, от которого производится
          наследование, называется базовым, родительским или суперклассом. Новый класс называется потомком, наследником
          или производным классом.
        </p>
        <p className={styles.description}>
          Если взять пример из жизни, у нас есть HTML-элементы. В следующих модулях мы узнаем что все они представлены
          объектами. У них есть общие свойства и методы для упраления состоянием. Вместо того чтобы добавлять общие
          свойства на каждый тип элемента, мы можем описать их в классе HTMLElement, и те элементы, которым нужен
          подобный интерфейс, могут наследовать от HTMLElement. Наследование помогает уменьшить объем повторяющегося
          кода.{' '}
        </p>
        <img className={styles.img} src={inheritanceImg} alt="inheritance" />
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.2.4 - Полиморфизм</h4>
        <p className={styles.description}>
          Любое обучение вождению не имело бы смысла, если бы человек, научившийся водить, скажем, Audi не мог потом
          водить BMW или любой другой автомобиль. С другой стороны, можно представить управление автомобилем используя
          джойстик или мышку с клавиатурой, а поведение автомобиля будет идентичным как если бы использовали руль.{' '}
        </p>
        <p className={styles.description}>
          Всё дело в том, что основные элементы управления автомобиля имеют одну и ту же конструкцию и принцип действия.
          По сути, можно сказать, что все автомобили имеют один и тот же интерфейс, а водитель, абстрагируясь от
          сущности автомобиля, работает именно с этим интерфейсом. Независимо от того, каким образом будет
          реализовываться движение и внутреннее функционирование машины, интерфейс останется прежним.{' '}
        </p>
        <p className={styles.description}>
          <b>Полиморфизм (polymorphism)</b> — это свойство системы позволяющее использовать объекты с одинаковым
          интерфейсом без информации о типе и внутренней структуре объекта. Позволяет переопределять в классах
          наследниках реализации методов базового класса.
        </p>
      </section>
    </section>
  </section>
))
